import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import {
  INITIAL_REFRESH_GATE,
  gateIdentifyRefresh,
  gateWantedRefresh,
} from "./personalization-refresh-gate";

/**
 * §4.5 (P4) — the post-hydration client trigger for the off-hot-path traits
 * refresh. Fires a single POST to /resources/personalization-refresh when:
 *   (a) SSR signaled "refresh wanted" (`pzRefreshWanted` from the root
 *       loader — no cookie, stale ts, or idh mismatch; the hec_pz cookie is
 *       HttpOnly so the client never parses it), AND
 *   (b) no in-session backoff marker is active (§4.3 — the endpoint's
 *       Retry-After is remembered in sessionStorage so a failing dependency
 *       costs at most one attempt per backoff window, never one per page).
 * At most one attempt per navigation, scheduled on requestIdleCallback
 * (timeout fallback) so it never competes with hydration.
 *
 * Consent: ADVISORY per D2 (2026-08-31 — hec_pz is first-party per legal;
 * nothing blocks client-side; downstream data teams own consent flags), so
 * no TrustArc gate here. Also re-fires on `analytics.js` identify() when the
 * identity it announces DIFFERS from the one the last refresh ran under —
 * the §1.7 pre-identify window's self-heal. Both triggers share ONE gate
 * (personalization-refresh-gate.ts): jam-4 (2026-09-04) showed every
 * signed-in DOCUMENT load firing two identical POSTs because each trigger
 * held its own private latch.
 */

const ENDPOINT = "/resources/personalization-refresh";
const BACKOFF_KEY = "hec_pz_refresh_na";

function backoffActive(): boolean {
  try {
    const na = Number(sessionStorage.getItem(BACKOFF_KEY) ?? 0);
    return na > Date.now();
  } catch {
    return false;
  }
}

function rememberBackoff(retryAfterSeconds: number): void {
  try {
    sessionStorage.setItem(
      BACKOFF_KEY,
      String(Date.now() + retryAfterSeconds * 1000),
    );
  } catch {
    /* storage unavailable — the server-side bo short-circuit still holds */
  }
}

async function fireRefresh(): Promise<void> {
  try {
    const res = await fetch(ENDPOINT, { method: "POST" });
    const retryAfter = Number(res.headers.get("Retry-After") ?? 0);
    if (retryAfter > 0) rememberBackoff(retryAfter);
  } catch {
    // Network failure: local 5-minute pause so a dead endpoint can't be
    // re-hit every navigation (the cookie carries no bo in this case).
    rememberBackoff(300);
  }
}

/** The Segment userId this browser currently carries — analytics.js's own
 * client-readable cookie (never HttpOnly). Null = anonymous. */
function readAjsUserId(): string | null {
  try {
    return (
      document.cookie
        .split(";")
        .map(c => c.trim())
        .find(c => c.startsWith("ajs_user_id="))
        ?.substring("ajs_user_id=".length) || null
    );
  } catch {
    return null;
  }
}

export function PersonalizationRefreshTrigger({ wanted }: { wanted: boolean }) {
  const location = useLocation();
  // ONE gate across both triggers (jam-4 dedupe) — checked at FIRE time, so
  // whichever trigger runs first consumes the navigation's attempt.
  const gateRef = useRef(INITIAL_REFRESH_GATE);
  const locationKeyRef = useRef(location.key);
  locationKeyRef.current = location.key;

  useEffect(() => {
    if (!wanted || backoffActive()) return;
    const fire = () => {
      const { fire: shouldFire, next } = gateWantedRefresh(gateRef.current, {
        locationKey: location.key,
        currentUserId: readAjsUserId(),
      });
      gateRef.current = next;
      if (shouldFire) void fireRefresh();
    };
    const idle = (
      window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: object) => number;
      }
    ).requestIdleCallback;
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (idle) {
      idleId = idle(fire, { timeout: 4000 });
    } else {
      timeoutId = window.setTimeout(fire, 2000);
    }
    return () => {
      if (idleId !== undefined) {
        (
          window as Window & { cancelIdleCallback?: (id: number) => void }
        ).cancelIdleCallback?.(idleId);
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
    // Re-arm once per navigation (§4.5: one attempt per navigation max —
    // the gate also counts a fire the identify trigger already made).
  }, [wanted, location.key]);

  // analytics.js identify → fire only when the announced identity DIFFERS
  // from the one the last refresh ran under (§1.7 self-heal preserved, the
  // jam-4 same-identity dup eliminated). Segment emits identify as
  // (userId, traits, options).
  //
  // REBINDS on `segment:ready` (jam-4 review P2): a TrustArc
  // submit_preferences re-init REPLACES `window.analytics` with a fresh
  // instance (segment/index.tsx loadSegment — its own comment notes old
  // listeners drop), so a once-only subscription would silently stop routing
  // identify/account-switch events through the gate. Each ready event binds
  // the CURRENT instance and off()s the previous one so a stale listener
  // can't double-fire. The gate STATE deliberately lives in gateRef, outside
  // this effect — a re-init's replayed identify (loadSegment re-identifies
  // the same hashedEmail) hits the same lastFiredUserId and stays deduped,
  // so a consent submit can't trigger a spurious refresh.
  useEffect(() => {
    type AnalyticsLike = {
      on?: (evt: string, cb: (...args: unknown[]) => void) => void;
      off?: (evt: string, cb: (...args: unknown[]) => void) => void;
    };
    const onIdentify = (userId: unknown) => {
      if (backoffActive()) return;
      const { fire: shouldFire, next } = gateIdentifyRefresh(gateRef.current, {
        locationKey: locationKeyRef.current,
        userId,
      });
      gateRef.current = next;
      if (shouldFire) void fireRefresh();
    };

    let bound: AnalyticsLike | null = null;
    const bind = () => {
      const analytics = (window as Window & { analytics?: AnalyticsLike })
        .analytics;
      if (!analytics?.on || analytics === bound) return;
      bound?.off?.("identify", onIdentify);
      analytics.on("identify", onIdentify);
      bound = analytics;
    };

    // The instance may already exist (mount after load); segment:ready then
    // covers every (re-)init — loadSegment dispatches it each time.
    bind();
    window.addEventListener("segment:ready", bind);
    return () => {
      window.removeEventListener("segment:ready", bind);
      bound?.off?.("identify", onIdentify);
    };
  }, []);

  return null;
}
